/**
 * notifications-bell.tsx — Chuông thông báo trên thanh điều hướng.
 *
 * Trước đây thông báo nằm trong một tab của màn Cài đặt, nhưng đó là thứ cần
 * liếc nhanh giữa lúc làm việc chứ không phải đi vào Cài đặt để xem — nên gom
 * hẳn vào chuông, kèm chấm đỏ đếm số chưa đọc.
 *
 * Backend: `GET /notifications`, `PATCH /notifications/:id/read`,
 * `POST /notifications/read-all`. Chưa có API cấu hình bật/tắt theo loại.
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Bell, BellRing, CheckCheck, CheckCircle2, MessageSquareHeart, Undo2, VolumeX, Wallet } from 'lucide-react'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loading, EmptyState } from '@/components/shared/feedback'
import {
  useNotifications, useMarkAllNotificationsRead, useMarkNotificationRead,
} from '@/hooks/use-settings'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { unlockNotifySound, useNotifySoundStore } from '@/lib/notify/notify-sound'
import { useAuthStore } from '@/stores/auth-store'
import { useMyNotifyPrefs } from '@/hooks/use-notification-settings'
import { bellBanner, useEnableNotifications } from '@/hooks/use-enable-notifications'
import { desktopPermission } from '@/components/shared/notification-center'
import { currentLocale, i18n } from '@/lib/i18n'
import { NOTI_TABS, tabFilter, tabUnread, typeLabel, type NotiTabKey } from '@/lib/notification-tabs'

/** Biểu tượng riêng cho loại thông báo cần nổi bật hơn một chấm xanh (loại khác giữ nguyên). */
const TYPE_ICON: Record<string, typeof Undo2> = {
  reclaim: Undo2,
  feedback: MessageSquareHeart,
  billing: Wallet,
  billing_paid: CheckCircle2,
}

function TypeIcon({ type }: { type: string }) {
  const Icon = TYPE_ICON[type]
  if (!Icon) return null
  return <Icon className={cn('h-3.5 w-3.5 shrink-0 self-center', type === 'billing_paid' ? 'text-success' : type === 'feedback' || type === 'billing' ? 'text-primary' : 'text-warning')} aria-label={typeLabel(type)} />
}

/** "3 phút", "2 giờ", "18/06" — đủ để biết mới hay cũ mà không dài dòng. */
function timeAgo(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const min = Math.floor((Date.now() - d.getTime()) / 60000)
  if (min < 1) return i18n.t('sharedMisc:notificationsBell.timeAgo.now')
  if (min < 60) return i18n.t('sharedMisc:notificationsBell.timeAgo.minutes', { count: min })
  const hours = Math.floor(min / 60)
  if (hours < 24) return i18n.t('sharedMisc:notificationsBell.timeAgo.hours', { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return i18n.t('sharedMisc:notificationsBell.timeAgo.days', { count: days })
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' })
}

/**
 * Dải "Bật thông báo" ngay trong khung chuông: người dùng muốn bật khỏi phải đi tìm trong Cài đặt cá nhân.
 * Nằm TRONG nội dung khung nên mỗi lần mở khung mới dựng → đọc lại quyền hiện tại của trình duyệt (quyền có thể vừa đổi
 * ở nơi khác: hộp xin quyền, ổ khoá cạnh thanh địa chỉ); sau khi bấm "Bật" thì đọc lại để dải tự biến mất.
 */
function EnableNotificationsBanner() {
  const { t } = useTranslation('sharedMisc')
  const user = useAuthStore((s) => s.user)
  const { data: prefs } = useMyNotifyPrefs(!!user)
  const { enable, busy } = useEnableNotifications()
  const [, setTick] = useState(0)
  const banner = bellBanner(desktopPermission(), !!prefs?.desktop, !!(user?.impersonatedBy || user?.platformActorId))
  if (banner === 'enable') {
    return (
      <div className="flex items-center gap-2 border-b bg-primary/5 px-3 py-2 text-xs">
        <BellRing className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1">{t('notificationsBell.enableBanner')}</span>
        <Button size="sm" className="h-6 px-2.5 text-xs" disabled={busy} onClick={() => { void enable().then(() => setTick((n) => n + 1)) }}>
          {t('notificationsBell.enable')}
        </Button>
      </div>
    )
  }
  if (banner === 'denied') {
    return (
      <div className="flex items-start gap-2 border-b bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
        <BellRing className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1">{t('notificationsBell.deniedBanner')}</span>
      </div>
    )
  }
  return null
}

export function NotificationsBell({
  className,
  label,
  side = 'right',
  align = 'end',
  badgeClassName,
}: {
  className?: string
  /** Chữ bên cạnh chuông khi đặt trong cột menu chính (ẩn/hiện theo cột). */
  label?: ReactNode
  /** Hướng bung bảng thông báo: cột menu → `right`; thanh tab dưới (điện thoại) → `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /**
   * Vị trí chấm đếm chưa đọc (toạ độ tuyệt đối trong nút). Bỏ trống → gắn vào góc trên-phải của BIỂU TƯỢNG chuông, nên số dài ("99+")
   * không còn tràn ra ngoài nút hẹp của cột menu (nút có `overflow-hidden` → "+" bị cắt, lỗi 15/10/2026).
   */
  badgeClassName?: string
}) {
  const { t } = useTranslation('sharedMisc')
  const [tab, setTab] = useState<NotiTabKey>('all')
  const filter = tabFilter(tab)
  const { data, isLoading } = useNotifications(20, filter)
  const markAll = useMarkAllNotificationsRead()
  const markOne = useMarkNotificationRead()
  const navigate = useNavigate()

  const anchoredToIcon = badgeClassName === undefined
  const markPos = badgeClassName ?? '-right-2 -top-1.5'
  const unread = data?.unreadCount ?? 0
  const tabUnreadNow = tabUnread(tab, data?.unreadByType) || (tab === 'all' ? unread : 0)
  // Trình duyệt chặn tự phát âm thanh tới khi người dùng tương tác → gợi ý nhẹ.
  const soundBlocked = useNotifySoundStore((s) => s.blocked)

  const handleMarkAll = async () => {
    try {
      await markAll.mutateAsync(filter)
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const open = (n: { id: string; isRead: boolean; link?: string | null }) => {
    if (!n.isRead) markOne.mutate(n.id)
    if (n.link) navigate(n.link)
  }

  const marks = (
    <>
      {soundBlocked && unread === 0 && <span className={cn('absolute h-2 w-2 rounded-full bg-amber-500', markPos)} aria-hidden />}
      {unread > 0 && (
        <span className={cn('absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground', markPos)}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" aria-label={t('notificationsBell.ariaLabel')} className={cn('relative', className)}>
          {anchoredToIcon ? (
            <span className="relative inline-flex shrink-0">
              <Bell className="h-5 w-5" />
              {marks}
            </span>
          ) : (
            <>
              <Bell className="h-5 w-5" />
              {marks}
            </>
          )}
          {label}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side={side} align={align} sideOffset={8} collisionPadding={8} className="w-[min(28rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2.5">
          <p className="flex-1 text-sm font-semibold">{t('notificationsBell.title')}</p>
          {tabUnreadNow > 0 && (
            <>
              <Badge variant="secondary" className="text-[10px]">{t('notificationsBell.unreadCount', { n: tabUnreadNow })}</Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleMarkAll}
                disabled={markAll.isPending}
              >
                <CheckCheck className="h-3.5 w-3.5" /> {t('notificationsBell.markAllRead')}
              </Button>
            </>
          )}
        </div>

        <div role="tablist" aria-label={t('notificationsBell.tablistAriaLabel')} className="flex justify-between overflow-x-auto border-b px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NOTI_TABS.map((tb) => {
            const n = tabUnread(tb.key, data?.unreadByType)
            const active = tab === tb.key
            return (
              <button
                key={tb.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(tb.key)}
                className={cn(
                  'relative flex shrink-0 items-center gap-1 px-1 py-2 text-[11.5px] transition-colors',
                  active ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tb.label}
                {n > 0 && (
                  <span className={cn('rounded-full px-1 text-[9.5px] font-medium leading-4 tabular-nums', active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                    {n > 99 ? '99+' : n}
                  </span>
                )}
                {active && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" />}
              </button>
            )
          })}
        </div>

        <EnableNotificationsBanner />

        {soundBlocked && (
          <div className="flex items-center gap-2 border-b bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
            <VolumeX className="h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0 flex-1">{t('notificationsBell.soundBlocked')}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => { void unlockNotifySound() }}
            >
              {t('notificationsBell.enableSound')}
            </Button>
          </div>
        )}

        <div className="max-h-[26rem] overflow-y-auto">
          {isLoading ? (
            <div className="p-6"><Loading label={t('notificationsBell.loading')} /></div>
          ) : !data?.notifications.length ? (
            <div className="p-4">
              <EmptyState icon={Bell} title={tab === 'all' ? t('notificationsBell.emptyAll') : t('notificationsBell.emptyTab', { tab: NOTI_TABS.find((x) => x.key === tab)?.label.toLowerCase() ?? '' })} />
            </div>
          ) : (
            <ul className="divide-y">
              {data.notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => open(n)}
                    className={cn(
                      'flex w-full gap-2 px-3 py-2.5 text-left transition-colors hover:bg-accent',
                      !n.isRead && 'bg-accent/40',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                        n.isRead ? 'bg-transparent' : 'bg-primary',
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <TypeIcon type={n.type} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{n.title}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{timeAgo(n.createdAt)}</span>
                      </span>
                      {n.body && (
                        <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>
                      )}
                      <span className="mt-1 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {typeLabel(n.type)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
